import ConfirmationButtons from "@/common/ConfirmationButtons";
import { Container } from "@/common/ui/container";
import OTPInputBox from "@/common/ui/otp/OTPInputBox";
import ResendOTP from "@/common/ui/otp/ResendOTP";
import FormRender from "@/components/Form/Renderer/RenderForm";
import Secure from "@/secure/payload";
import { privateResendCode, sendChangePhoneOTP, verifyChangePhoneOTP } from "@/services/common/security.services";
import { toastMessage } from "@/utils/helper/myToast";
import React, { useState } from "react";

interface ChangePhoneProps {
  onClose: (props?: any) => void;
}
interface VerifyNewPhoneProps {
  handleVerifyNewPhone: (otp: string) => void;
  verifyData: any;
  onClose: (props?: any) => void;
  isLoading: boolean;
  error: string;
  setError: (text: string) => void;
}

const ChangePhoneConfig = {
  submitBtnText: "Next",
  showLabel: true,
  sections: [
    {
      widgets: [
        {
          widgetType: "phone",
          label: "Phone",
          field: "phNo",
          placeholder: "Phone number",
          validation: {
            required: true,
          },
          defaultCountry: {
            cid: "99",
            iso_name: "IN",
            name: "INDIA",
            nicename: "India",
            country_name_code: "IND",
            country_num_code: "356",
            country_phone_code: "+91",
            is_active: "1",
            sms_gateway: 1,
          },
        },
      ],
    },
  ],
};

const VerifyNewPhone: React.FC<VerifyNewPhoneProps> = ({ handleVerifyNewPhone, verifyData, onClose, isLoading, error, setError }) => {
  const [otp, setOtp] = useState("");
  const [resetCount, setResetCount] = useState(0);
  const [enabledMfa, setEnableMfa] = useState(false);
  const isSMS2FEnabled = verifyData?.isMfaSmsEnabled && JSON.parse(verifyData.isMfaSmsEnabled);

  const resendOtp = async () => await privateResendCode({ token: verifyData?.changePhoneVerifyToken });

  const handleConfirm = () => {
    if (isSMS2FEnabled) {
      if (!enabledMfa) return;
    }
    handleVerifyNewPhone(otp);
  };

  return (
    <Container.ContentBox>
      <p className=" text-center py-5 text-13 text-text-label">
        We have sent a code to <span className=" font-medium">{verifyData?.maskIdentity}</span>.<br /> Please enter it below.
      </p>
      <div className=" flex flex-col w-full px-2 mb-4 justify-start">
        <OTPInputBox resetCount={resetCount} setCompleteOtp={setOtp} errorMessage={error} resetErrorMessage={() => setError("")} errorTextClassName="px-2" />
        <ResendOTP setResetCount={setResetCount} resendOtpAPI={resendOtp} className="md:px-1.5" errorMessage={error} resetErrorMessage={() => setError("")} />
      </div>
      {/* Remember me */}
      {isSMS2FEnabled && (
        <div className="flex items-center gap-2 py-2 mt-3 w-full px-[18px]">
          <div className="checkbox-wrapper-23" onClick={() => setEnableMfa((pre) => !pre)}>
            <input id={`remember-check`} checked={enabledMfa} type="checkbox" onChange={() => setEnableMfa((pre) => !pre)} />
            <label htmlFor={`remember-check`}>
              <svg viewBox="0,0,50,50" style={{ pointerEvents: "all", cursor: "pointer" }}>
                <path d="M5 30 L 20 45 L 45 5"></path>
              </svg>
            </label>
          </div>
          <span className="text-text-default text-sm select-none">I confirm that two-factor authentication will be linked to my new phone number.</span>
        </div>
      )}
      <Container.Separator />
      <ConfirmationButtons handleConfirm={handleConfirm} handleCancel={onClose} confirmText="Verify" disabled={!otp} isLoading={isLoading} />
    </Container.ContentBox>
  );
};

interface STEP {
  state: "Phone" | "Verify";
  props?: any;
}

const ChangePhone: React.FC<ChangePhoneProps> = ({ onClose }) => {
  const [step, setStep] = useState<STEP>({ state: "Phone" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFormSubmit = async (data: any) => {
    const res: any = await sendChangePhoneOTP(data);
    if (res?.status === 200) {
      const { message, isError, showMessage, data } = res.data;
      if (showMessage) toastMessage(message, isError);
      setStep({ state: "Verify", props: Secure.payloadDecryption(data) });
    } else {
      console.log("Error", res);
      const { message, isError } = res?.response?.data || {};
      if (message) {
        return toastMessage(message, isError);
      }
      if (res.message) {
        return toastMessage(res.message, true);
      }
    }
  };

  const handleVerifyNewPhone = async (otp: string) => {
    setIsLoading(true);
    try {
      const res: any = await verifyChangePhoneOTP({ otp, token: step.props?.changePhoneVerifyToken });

      if (res?.status === 200) {
        const { message, isError } = res.data;
        onClose({ disabledWarning: true });
        toastMessage(message, isError);
      } else if (res?.response?.status === 401) {
        const { message } = res?.response?.data || {};
        setError(message);
        return;
      } else {
        const { message } = res?.response?.data || {};
        if (message) {
          setError(message);
        }
      }
    } catch (error) {
      console.error("Verification error", error);
      toastMessage("An error occurred during verification.", true);
    } finally {
      setIsLoading(false);
    }
  };

  switch (step.state) {
    case "Phone":
      return (
        <FormRender
          useFormConfig={{ mode: "onSubmit", reValidateMode: "onChange", criteriaMode: "all" }}
          handleFormSubmit={handleFormSubmit}
          handleCancel={onClose}
          formConfig={ChangePhoneConfig}
          className="m-4 pt-0"
          formClassName=""
        />
      );
    case "Verify":
      return (
        <VerifyNewPhone
          handleVerifyNewPhone={handleVerifyNewPhone}
          verifyData={step.props}
          onClose={onClose}
          isLoading={isLoading}
          setError={setError}
          error={error}
        />
      );
    default:
      return null;
  }
};

export default ChangePhone;
